import type { ReactNode } from 'react';
import type { carTitle } from './documentTitle';
import type { ApiListingItem } from 'src/types/listing';
import type { ApiRentalListingFull } from 'src/types/rental';
import type { ListingCardProps } from 'src/components/ListingCard';

import { Fragment, createElement } from 'react';

import {
  ListingCardPhoto,
  ListingCardAmount,
  ListingCardNoPhoto,
  ListingCardPriceLabel,
} from 'src/components/ListingCard';

import { formatImageUrl } from './format-image';
import { fromDailyPrice } from './rentalPricing';
import { formatMoney, formatAmount } from './money';
import { rentedBadgeLabel } from './rentalOccupancy';
import { noPhotoAlt, carPhotoAlt } from './imageAlt';
import { rentalCarTermsLine } from './rentalCarTerms';
import { fuelLabel, gearboxLabel } from './carAttributes';

/**
 * What each vertical puts into the one listing card (`ListingCard`, ergonomics §3.1): the sale
 * feed, the rental feed and the dealer page fill the same slots from their own listing.
 *
 * Both verticals fill the price the same way — a label, then the number (decision №33): "Price:
 * 28,000 USD", "Per day: from 60 USD". The card sets the number in Big Shoulders; the label and
 * the second line stay Public Sans. Currency is the listing's own code as text: the card shows
 * what the car costs, not the token a deal later settles in.
 */
export type ListingCardSlots = Pick<ListingCardProps, 'media' | 'title' | 'price' | 'secondaryLine' | 'status'> & {
  to: string;
};

type Pictured = Parameters<typeof carTitle>[0] & { images?: string[] | null };

/** The first photo, or a frame that says there is none yet. */
const cardMedia = (car: Pictured): ReactNode => {
  const photo = car.images?.[0];

  return photo
    ? createElement(ListingCardPhoto, { src: formatImageUrl(photo), alt: carPhotoAlt(car) })
    : createElement(ListingCardNoPhoto, { label: noPhotoAlt(car) });
};

/** The price row of either vertical: its label, then the number the e2e suite reads as the amount. */
const priceRow = (label: string, amount: string): ReactNode =>
  createElement(
    Fragment,
    null,
    createElement(ListingCardPriceLabel, null, label),
    ' ',
    createElement(ListingCardAmount, null, amount)
  );

/**
 * The second line of a sale card (decision №38): the mileage, then the gearbox and the fuel —
 * "16,000 km · Automatic · Diesel" — each only when the dealer gave it, and no line when none.
 * The body stays on the listing itself: the photo already shows it.
 */
const saleSecondaryLine = (item: ApiListingItem): string | undefined => {
  const parts = [
    item.mileage != null ? `${formatAmount(item.mileage)} km` : undefined,
    gearboxLabel(item.gearbox),
    fuelLabel(item.fuel),
  ].filter((part): part is string => !!part);

  return parts.length > 0 ? parts.join(' · ') : undefined;
};

/** A car for sale: its price in the listing's currency, and what the dealer said of the car beneath. */
export const saleCardSlots = (item: ApiListingItem): ListingCardSlots => ({
  title: `${item.make.name} ${item.model.name} ${item.year}`,
  price: priceRow('Price:', formatMoney(item.price, item.currency)),
  secondaryLine: saleSecondaryLine(item),
  to: `/listing/${item.id}`,
  media: cardMedia(item),
});

/**
 * A car for rent: the daily rate behind its label — "from" when the dealer's grid varies with the
 * length of the booking — the deposit and the city beneath, then the seats and the daily kilometre
 * allowance the dealer gave (api#397: "5 seats · 300 km/day", the two questions a renter asks of a
 * car), and occupancy when the car is out now. The body, gearbox and fuel stay on the listing.
 */
export const rentalCardSlots = (
  listing: ApiRentalListingFull,
  dayOf?: (moment: string) => string
): ListingCardSlots => {
  const { price, isFrom } = fromDailyPrice(listing);
  const location = listing.location?.trim();
  const terms = rentalCarTermsLine(listing);

  return {
    title: `${listing.make.name} ${listing.model.name}${listing.year ? ` ${listing.year}` : ''}`,
    price: priceRow('Per day:', `${isFrom ? 'from ' : ''}${formatAmount(price)} ${listing.currency}`),
    secondaryLine: `Deposit: ${formatAmount(listing.depositAmount)} ${listing.currency}${
      location ? ` · ${location}` : ''
    }${terms ? ` · ${terms}` : ''}`,
    // A busy car stays in the feed on purpose — it can still be booked for later dates — so the
    // card, not its absence, carries the occupancy.
    status: rentedBadgeLabel(listing.rentedNow, listing.rentedUntil, dayOf) ?? undefined,
    to: `/rentals/listing/${listing.id}`,
    media: cardMedia(listing),
  };
};
